<script setup>
import {
  ref,
  reactive,
  onMounted,
  watch
} from "vue";
import Label from "@/components/Label";
import VueSelect from "vue-select";
import { api } from "@/services/api";
import { utils } from "@/utilities.js";

import { useVillageStore } from "@/stores/village";
import { useHybridStore } from "@/stores/hybrid";
import { useCompetitorStore } from "@/stores/competitor";
import { useContextStore } from "@/stores/context";

const contextStore = useContextStore();

const villageStore = useVillageStore();
const hybridStore = useHybridStore();
const competitorStore = useCompetitorStore();

const villageSelected = ref({});
const villageReady = ref(false);
let villagesOptions = reactive([]);

const hybridReady = ref(false);
let hybridsOptions = reactive([]);

const competitorSelected = ref([]);
const competitorReady = ref(false);
let competitorsOptions = reactive([]);

const props = defineProps(["myLocation", "activity"]);
const emits = defineEmits(["submitMaster"]);

let hybridReservedForm = reactive({
  name: "",
  hybrid_name: "",
  reserved: 0,
});

const form = reactive({
  activity_type: "Farmer Meeting",
  date: utils.getCurrentDate(),

  village_id: props.activity.schedulable_id,
  attendance: 0,
  hybrids: "",
  feedback: "",
  budget_spent: props.activity?.budget ? parseFloat(props.activity.budget) : 0,
  competitors: [],
  hybrid_reserveds: [],

  description: "",
  files: "",

  check_out: true,
  sale_schedule: props.activity,

  latitude: props.myLocation.latitude,
  longitude: props.myLocation.longitude,
  current_latitude: props.myLocation.current_latitude,
  current_longitude: props.myLocation.current_longitude,
  distance: utils.getDistanceFromActivity(props.activity),

  isLaterActive: true,
});

onMounted(async () => {
  await villageStore.initVillages();
  villagesOptions = villageStore.getVillages();

  if (villagesOptions.length == 0 || contextStore.getIsOffline == false) {
    villagesOptions = await api.getVillages();
    villagesOptions.forEach((data) => {
      villageStore.addNewVillage(data);
    });
  }

  await hybridStore.initHybrids();
  hybridsOptions = hybridStore.getHybrids();

  if (hybridsOptions.length == 0 || contextStore.getIsOffline == false) {
    hybridsOptions = await api.getHybrids();
    hybridsOptions.forEach((data) => {
      hybridStore.addNewHybrid(data);
    });
  }

  await competitorStore.initCompetitors();
  competitorsOptions = competitorStore.getCompetitors();

  if (competitorsOptions.length == 0 || contextStore.getIsOffline == false) {
    competitorsOptions = await api.getCompetitors();
    competitorsOptions.forEach((data) => {
      competitorStore.addNewCompetitor(data);
    });
  }

  villagesOptions = await utils.convertToSelectObject(villagesOptions);
  hybridsOptions = await utils.convertToSelectObject(hybridsOptions);
  competitorsOptions = await utils.convertToSelectObject(competitorsOptions);

  villageReady.value = true;
  hybridReady.value = true;
  competitorReady.value = true;
});

watch(
  () => villageReady.value,
  (n, o) => {
    if (form.village_id && n) {
      const d = villagesOptions.filter((e) => e.value == form.village_id);
      villageSelected.value = d[0] ?? {};
    }
  }
);

const submit = async () => {
  form.village_id = villageSelected?.value?.value;
  competitorSelected.value.forEach((x) => {
    form.competitors.push(x.value);
  });

  emits('submitMaster', form);
  return;
};

const addHybridReserved = () => {
  if (
    hybridReservedForm.name == "" ||
    hybridReservedForm.hybrid_name == "" ||
    hybridReservedForm.reserved == ""
  ) {
    alert("please fill all inputs.");
    return;
  }

  form.hybrid_reserveds.push(hybridReservedForm);
  hybridReservedForm = {
    name: "",
    hybrid_name: "",
    reserved: 0,
  };
};

const removeHybridReserved = (index) => {
  form.hybrid_reserveds.splice(index, 1);
};
</script>
<template>
  <form class="space-y-4 text-left" action="#" @submit.prevent="submit">
    <div class="grid grid-cols-6 gap-6">
      <div class="col-span-6 sm:col-span-2">
        <Label value="Distance (Km)" />
        <input
          class="mt-1 block w-full rounded-lg resize-y"
          type="text"
          v-model="form.distance"
        />
      </div>

      <div class="col-span-6 sm:col-span-2">
        <Label for="date" value="Date" />
        <input
          id="date"
          type="date"
          :max="utils.getCurrentDate()"
          class="mt-1 block w-full rounded-lg"
          v-model="form.date"
          disabled
        />
      </div>
      <div class="col-span-6 sm:col-span-2">
        <Label for="activity_type" value="Activity Type" />
        <input
          id="activity_type"
          type="text"
          v-model="form.activity_type"
          class="mt-1 block w-full rounded-lg"
          disabled
        />
      </div>
      <div class="col-span-6 sm:col-span-6">
        <Label for="village_id" value="Village" class="required" />
        <VueSelect
          v-if="villageReady"
          v-model="villageSelected"
          :options="villagesOptions"
          label="label"
          value="value"
          placeholder="Select"
          :searchable="true"
          class="block w-full my-1 bg-white rounded-lg"
        >
          <template #search="{ attributes, events }">
            <input
              class="vs__search"
              :required="!villageSelected"
              v-bind="attributes"
              v-on="events"
            />
          </template>
        </VueSelect>
      </div>
      <div class="col-span-6 sm:col-span-6">
        <Label for="description" value="Description" class="required" />
        <textarea
          name=""
          id="description"
          cols="30"
          rows="5"
          class="mt-1 w-full rounded-lg"
          v-model="form.description"
          required
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="feedback" value="Feedback" />
        <input
          id="feedback"
          type="text"
          class="mt-1 block w-full rounded-lg"
          v-model="form.feedback"
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="hybrids" value="Hybrids" class="required" />
        <input
          id="hybrids"
          type="text"
          class="mt-1 w-full rounded-lg"
          required
          v-model="form.hybrids"
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="attendance" value="Attendance" class="required" />
        <input
          id="attendance"
          type="number"
          class="mt-1 block w-full rounded-lg"
          v-model="form.attendance"
          oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
          @focus="utils.onFocusDefaultVal($event)"
          maxlength="5"
          min="0"
          required
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="start_date" value="Upload Media" />
        <input
          id="files"
          type="file"
          class="mt-1 block w-full rounded-lg shadow-sm"
          @input="form.files = Array.from($event.target.files)"
          multiple
        />
        <p class="text-sm text-gray-500 mt-2">max size per file: 10mb</p>
        <!-- <template v-for="(f, index) in form.files" :key="f.file">
        <input-error
          :message="form.errors?.[`files.${index}`]"
          class="mt-2"
          v-if="form.errors?.[`files.${index}`]"
        />
      </template> -->
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="budget_spent" value="Budget Spend" class="required" />
        <input
          id="budget_spent"
          type="number"
          class="mt-1 block w-full rounded-lg"
          v-model="form.budget_spent"
          oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
          @focus="utils.onFocusDefaultVal($event)"
          maxlength="7"
          min="0"
          required
        />
      </div>

      <div class="col-span-6 sm:col-span-3">
        <Label for="competitors" value="Key Competitors" class="required" />
        <VueSelect
          v-if="competitorReady"
          v-model="competitorSelected"
          :options="competitorsOptions"
          label="label"
          value="value"
          placeholder="Select"
          :searchable="true"
          multiple
          class="block w-full my-1 bg-white rounded-lg"
        >
          <template #search="{ attributes, events }">
            <input
              class="vs__search"
              :required="competitorSelected.length == 0"
              v-bind="attributes"
              v-on="events"
            />
          </template>
        </VueSelect>
      </div>

      <!-- Hybrid Reserved -->
      <div class="col-span-6 sm:col-span-3">
        <Label for="Hybrid Reserve" value="Booking (kg)" />
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Name</th>
              <th>Hybrid</th>
              <th>Booking (Kg)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <input
                  type="text"
                  class="mt-1 block w-full rounded-lg"
                  v-model="hybridReservedForm.name"
                />
              </td>
              <td>
                <select
                  name=""
                  class="mt-1 block w-full rounded-lg"
                  v-model="hybridReservedForm.hybrid_name"
                  id=""
                >
                  <option value=""></option>
                  <option
                    :value="hybrid.label"
                    v-for="hybrid in hybridsOptions"
                    :key="hybrid.value"
                  >
                    {{ hybrid.label }}
                  </option>
                </select>
              </td>
              <td>
                <input
                  type="number"
                  class="mt-1 block w-full rounded-lg"
                  v-model="hybridReservedForm.reserved"
                  oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                  @focus="utils.onFocusDefaultVal($event)"
                  maxlength="5"
                  min="0"
                />
              </td>
              <td>
                <button
                  class="inline-block items-center px-4 py-2 border border-transparent rounded-lg font-semibold text-xs tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25 transition ml-2 bg-black text-white"
                  @click.prevent="addHybridReserved()"
                >
                  +
                </button>
              </td>
            </tr>
            <tr
              v-for="(hybrid, index) in form.hybrid_reserveds"
              :key="hybrid.name + hybrid.hybrid_name"
              class="border-b-2 my-2 py-2"
            >
              <td>{{ hybrid.name }}</td>
              <td>{{ hybrid.hybrid_name }}</td>
              <td>{{ hybrid.reserved }}</td>
              <td>
                <button
                  class="inline-block items-center px-4 py-2 border border-transparent rounded-lg font-semibold text-xs tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25 transition ml-2 bg-black text-white"
                  @click.prevent="removeHybridReserved(index)"
                >
                  -
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-end col-span-6">
        <button
          type="submit"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-lg font-semibold text-xs tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25 transition ml-2 bg-green-500 text-white"
        >
          Check Out
        </button>
      </div>
    </div>
  </form>
</template>
